import { useState, useEffect, useRef, useCallback } from "react";
import { Ruler, Plus, Trash2, MessageSquare, Check, CloudOff, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { updateQuote } from "@/lib/api";
import { toast } from "sonner";

const UNITS = ["mm", "cm", "m", "m2", "m3"];

// localStorage key for the offline-queued payload (one per quote id).
const offlineKey = (id) => `mobile_measurements_pending_${id}`;
const newUid = () => `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Mobile Measurements editor — auto-saves with 600ms debounce, queues to
 * localStorage if offline, replays on `online` event.
 *
 * Props:
 *   quoteId           job id
 *   initial           initial measurements array (from the loaded quote)
 *   onSyncedExternal  called after a successful save so parent can refresh
 *                     the quote object (used for "Saved" UI confirmation).
 */
export default function MobileMeasurements({ quoteId, initial, onSyncedExternal }) {
  // Local working copy. Each row carries a stable _uid (generated on first
  // touch) so React keys and concurrent-edit reconciliation work consistently.
  const [rows, setRows] = useState(() =>
    (initial || []).map((m) => ({ ...m, _uid: m._uid || newUid() }))
  );
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);

  // Refs for the debounce + the latest rows snapshot (handlers stay stable).
  const debounceRef = useRef(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // When the parent re-loads the quote (e.g. office edited on desktop while
  // mobile was idle), seed our state with the new data BUT only if the
  // textarea isn't currently focused — otherwise we'd yank a row mid-edit.
  useEffect(() => {
    if (!initial) return;
    const isFocused = document.activeElement && document.activeElement.closest("[data-measurement-row]");
    if (isFocused) return;
    setRows((initial || []).map((m) => ({ ...m, _uid: m._uid || newUid() })));
  }, [initial]);

  // Listen for online/offline transitions. On reconnect, replay any pending
  // payload from localStorage before resuming normal saves.
  useEffect(() => {
    const onOnline = async () => {
      setOffline(false);
      const pending = localStorage.getItem(offlineKey(quoteId));
      if (pending) {
        try {
          const payload = JSON.parse(pending);
          await updateQuote(quoteId, { measurements: payload });
          localStorage.removeItem(offlineKey(quoteId));
          setSavedAt(new Date());
          toast.success("Offline measurements synced.");
          onSyncedExternal?.();
        } catch (e) {
          console.error("Failed to replay offline measurements", e);
        }
      }
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [quoteId, onSyncedExternal]);

  // Debounced save (600ms idle). Anytime we touch rows, this fires.
  const scheduleSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload = rowsRef.current;
      if (offline || (typeof navigator !== "undefined" && !navigator.onLine)) {
        // Stash for replay when signal returns. We overwrite — only the latest
        // payload matters; older queued payloads are stale by definition.
        localStorage.setItem(offlineKey(quoteId), JSON.stringify(payload));
        return;
      }
      try {
        setSaving(true);
        await updateQuote(quoteId, { measurements: payload });
        setSavedAt(new Date());
        localStorage.removeItem(offlineKey(quoteId));
      } catch (e) {
        console.error(e);
        // Treat unreachable backend as offline and stash for retry.
        localStorage.setItem(offlineKey(quoteId), JSON.stringify(payload));
      } finally {
        setSaving(false);
      }
    }, 600);
  }, [quoteId, offline]);

  // Flush any pending save on unmount so leaving the page mid-debounce doesn't
  // drop the user's last keystroke.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        const payload = rowsRef.current;
        // Fire-and-forget; on real navigation the fetch may not complete but
        // we also stash to localStorage which the next mount will replay.
        try {
          localStorage.setItem(offlineKey(quoteId), JSON.stringify(payload));
          updateQuote(quoteId, { measurements: payload }).then(() => {
            localStorage.removeItem(offlineKey(quoteId));
          }).catch(() => {});
        } catch (e) { /* ignore */ }
      }
    };
  }, [quoteId]);

  const addRow = () => {
    setRows((prev) => [...prev, { _uid: newUid(), label: "", value: "", unit: "m", notes: "", photo_url: "" }]);
    scheduleSave();
  };
  const updateRow = (uid, patch) => {
    setRows((prev) => prev.map((r) => (r._uid === uid ? { ...r, ...patch } : r)));
    scheduleSave();
  };
  const removeRow = (uid) => {
    setRows((prev) => prev.filter((r) => r._uid !== uid));
    scheduleSave();
  };

  const savedLabel = (() => {
    if (offline) return "Offline — will sync when online";
    if (saving) return "Saving…";
    if (!savedAt) return "Auto-saves as you type";
    const secs = Math.floor((Date.now() - savedAt.getTime()) / 1000);
    if (secs < 5) return "Saved";
    if (secs < 60) return `Saved ${secs}s ago`;
    return `Saved ${Math.floor(secs / 60)}m ago`;
  })();

  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: "var(--border)" }} data-testid="mobile-measurements">
      <div className="flex items-center justify-between mb-3">
        <h3 className="ui-label flex items-center gap-1.5">
          <Ruler className="w-3.5 h-3.5" /> Measurements
        </h3>
        <div className="flex items-center gap-1.5 text-[11px]" style={{ color: offline ? "#C2410C" : "var(--text-muted)" }}>
          {offline ? (
            <><CloudOff className="w-3 h-3" /> {savedLabel}</>
          ) : saving ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> {savedLabel}</>
          ) : savedAt ? (
            <><Check className="w-3 h-3" style={{ color: "#16A34A" }} /> {savedLabel}</>
          ) : (
            <>{savedLabel}</>
          )}
        </div>
      </div>

      {rows.length === 0 && (
        <p className="text-sm py-4 text-center" style={{ color: "var(--text-muted)" }}>
          No measurements yet. Tap Add Measurement to start.
        </p>
      )}

      <div className="space-y-2">
        {rows.map((m) => (
          <div
            key={m._uid}
            data-measurement-row
            data-testid={`mobile-measurement-${m._uid}`}
            className="rounded-lg p-3"
            style={{ background: "#FAFAFA", border: "1px solid var(--border)" }}
          >
            <div className="flex gap-2">
              <Input
                value={m.label}
                onChange={(e) => updateRow(m._uid, { label: e.target.value })}
                placeholder="Label (e.g. Living room width)"
                className="h-10 rounded-lg text-sm flex-1"
                data-testid={`mobile-meas-label-${m._uid}`}
              />
              <button
                type="button"
                onClick={() => removeRow(m._uid)}
                className="h-10 w-10 flex items-center justify-center rounded-lg text-red-500 active:scale-95"
                style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
                data-testid={`mobile-meas-remove-${m._uid}`}
                aria-label="Remove measurement"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={m.value}
                onChange={(e) => updateRow(m._uid, { value: e.target.value })}
                placeholder="Value"
                inputMode="decimal"
                className="h-10 rounded-lg text-sm font-mono flex-1"
                data-testid={`mobile-meas-value-${m._uid}`}
              />
              <Select value={m.unit || "m"} onValueChange={(v) => updateRow(m._uid, { unit: v })}>
                <SelectTrigger className="h-10 rounded-lg text-sm w-24" data-testid={`mobile-meas-unit-${m._uid}`}>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-2 mt-2">
              <MessageSquare className="w-3.5 h-3.5 mt-2.5" style={{ color: "var(--text-muted)" }} />
              <Input
                value={m.notes || ""}
                onChange={(e) => updateRow(m._uid, { notes: e.target.value })}
                placeholder="Note (optional — context for the office)"
                className="h-9 rounded-lg text-xs italic flex-1"
                style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}
                data-testid={`mobile-meas-notes-${m._uid}`}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="w-full mt-3 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-95"
        style={{ background: "var(--primary-light)", color: "var(--primary)", border: "1px dashed var(--primary)" }}
        data-testid="mobile-meas-add"
      >
        <Plus className="w-4 h-4" /> Add Measurement
      </button>
    </div>
  );
}
